import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FunctionField, NumberField, TextField, TopToolbar, usePermissions, useTranslate } from 'react-admin';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { RetailChainLogoField } from '@/components/retailChain/fields/RetailChainLogoField.jsx';
import { RetailChainBrandsField } from '@/components/retailChain/fields/RetailChainBrandsField.jsx';
import { RetailChainMoreActions } from '@/components/retailChain/buttons/RetailChainMoreActions.jsx';
import { getRetailChainFilters, RETAIL_CHAIN_SEARCH_FIELDS } from '@/components/retailChain/list/retailChainFilters.jsx';
import { useDrawer } from '@/composables/useDrawer';

const StopRowClick = ({ children }) => (
    <Box onClick={event => event.stopPropagation()} sx={{ display: 'inline-flex', maxWidth: '100%' }}>
        {children}
    </Box>
);

StopRowClick.propTypes = {
    children: PropTypes.node,
};

const ListActions = () => {
    const { permissions } = usePermissions();
    const translate = useTranslate();

    return (
        <TopToolbar>
            <SearchFilterInput searchFields={RETAIL_CHAIN_SEARCH_FIELDS} />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            {permissions?.isAdmin ? (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.retailChain.labels.create')} />
                </CustomToolbarActionsPopover>
            ) : null}
        </TopToolbar>
    );
};

export const RetailChainList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const filters = useMemo(() => getRetailChainFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) {
        return null;
    }

    if (!permissions?.isAdmin && !permissions?.isBok) {
        return null;
    }

    return (
        <CustomList
            resource='retail-chain'
            title={translate('menu.retailChains')}
            filters={filters}
            actions={<ListActions />}
            sort={{ field: 'name', order: 'ASC' }}
            empty={false}
        >
            <CustomDatagridConfigurable
                header={<CustomDatagridHeader />}
                bulkActionButtons={false}
                rowClick={id => {
                    openDrawer(id, 'retail-chain-show');
                    return false;
                }}
            >
                <FunctionField
                    source='logo'
                    label={translate('resources.retailChain.fields.logo')}
                    sortable={false}
                    render={record => (
                        <StopRowClick>
                            <RetailChainLogoField key={record?.id} />
                        </StopRowClick>
                    )}
                />
                <TextField source='name' label={translate('resources.retailChain.fields.name')} sortBy='name' sx={{ fontWeight: '600 !important' }} />
                <FunctionField
                    source='brands'
                    label={translate('resources.retailChain.fields.brands')}
                    sortable={false}
                    render={() => (
                        <StopRowClick>
                            <RetailChainBrandsField />
                        </StopRowClick>
                    )}
                />
                <FunctionField
                    source='contractorCount'
                    label={translate('resources.retailChain.fields.contractorCount')}
                    sortBy='contractorCount'
                    render={() => (
                        <StopRowClick>
                            <NumberField source='contractorCount' emptyText='0' />
                        </StopRowClick>
                    )}
                />
                <FunctionField
                    source='collectionPointCount'
                    label={translate('resources.retailChain.fields.collectionPointCount')}
                    sortBy='collectionPointCount'
                    render={() => (
                        <StopRowClick>
                            <NumberField source='collectionPointCount' emptyText='0' />
                        </StopRowClick>
                    )}
                />
                <FunctionField
                    label={translate('common.options')}
                    sortable={false}
                    render={() => (
                        <Box onClick={event => event.stopPropagation()}>
                            <RetailChainMoreActions />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};
